// This was generated during the migration to @kiwicom/eslint-config
// PLEASE REMOVE THE GLOBAL ESLINT DISABLE BELOW
// AND FIX ERRORS ACCORDINGLY.
/* eslint-disable multiline-comment-style, babel/camelcase */
/* TRACKING EVENTS STRUCTURE
Please ensure that each new tracking event has:

category: module which the event concerns (REQUIRED)
subcategory: deprecated
destinations: where the event should be sent (REQUIRED)
owner: a group of people that is responsible for the event (REQUIRED)
triggerCondition: when exactly is the event triggered from the technical perspective (REQUIRED)
description: general description of the event (REQUIRED)
version: integer that is increased by 1 each time the event is updated (REQUIRED)
detail: deprecated

example:
________
category: "Booking",
action: "Booking started new",
destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
owner: "@vlado",
version: 1,
triggerCondition: "after we process data from first check_flights API call response",
description: "booking loader finished loading"
*/

import type { ClientEvent } from "@kiwicom/nitro/lib/records/Event"
import { TrackingModule } from "@kiwicom/frontend-mmb/services/tracking/constants/trackingEventTypes"

// keep lowercase for backward compatibility usage
export const PAYMENT_ERROR_TYPES = {
  // USER ERRORS
  // ===
  // We receive usually useful info such as
  // CARD_EXPIRED, CARD_CVV_INCORRECT, CARD_EXCEEDED_LIMITATION
  USER: "user",

  // SYSTEM ZOOZ ERRORS
  // ===
  // Nonsense errors such as
  // "Call voice authorization number.", "Terminal ID unknown"
  SYSTEM: "system",

  // XHR ERRORS
  // Zooz / BE Api down
  XHR: "xhr",

  // NOT HONOURED ERRORS
  // Previously we tried to fix these errors via filling more details about the payment
  // Still used (for example in src/booking/services/reservation/actions/handleBookingPaymentError.js)
  // do not comment out until it is removed from whole codebase!
  NOT_HONOURED: "not_honoured", // Additional billing requirements

  // UNKNOWN ERRORS
  // Just fallback not happening too often
  // ===
  UNKNOWN: "unknown",
} as const

export const PAYMENT_ERROR_KEY = {
  // @ts-expect-error: Flow to TS migration.
  BANK_REFUSED: __("common.payment_error.bank_refused"),
  // @ts-expect-error: Flow to TS migration.
  CARD_CVV_INCORRECT: __("common.payment_error.card_cvv_incorrect"),
  // @ts-expect-error: Flow to TS migration.
  CARD_EXCEEDED_LIMITATION: __("common.payment_error.card_exceeded_limitation"),
  // @ts-expect-error: Flow to TS migration.
  CARD_EXPIRED: __("common.payment_error.card_expired"),
  // @ts-expect-error: Flow to TS migration.
  CARD_FUNDS: __("common.payment_error.card_funds"),
  // @ts-expect-error: Flow to TS migration.
  CARD_INVALID: __("common.payment_error.card_invalid"),
  // @ts-expect-error: Flow to TS migration.
  CARD_REPORTED: __("common.payment_error.card_reported"),
  // @ts-expect-error: Flow to TS migration.
  CARD_SECURITY: __("common.payment_error.card_security"),
  // @ts-expect-error: Flow to TS migration.
  FRAUD: __("common.payment_error.fraud"),
} as const

// https://developers.paymentsos.com/docs/api/#/introduction/responses/response-categories
export const paymentPosErrorResponses = {
  api_authentication_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  api_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  api_request_eSrror: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_authentication_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_network_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_request_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  payment_method_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  payment_method_declined: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },

  // Only these subcategories are useful for customer
  invalid_card_number: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID,
  },
  invalid_card_cvv: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_CVV_INCORRECT,
  },
  expired_card: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_EXPIRED,
  },
  declined_by_issuing_bank: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID, // TODO: Prepare new translation - add also to Zooz
  },
  declined_by_acquiring_bank: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID, // TODO: Prepare new translation
  },
  declined_on_fraud_check: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.FRAUD,
  },
  blocked_by_decision_engine: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID,
  },
} as const

export type CuckooEventDestinations = {
  logmole?: boolean
  exponea?: boolean
  ga?: boolean
  bigQuery?: boolean
  datadog?: boolean
}

export type EventCategory =
  | "refund"
  | "assisted refund"
  | "paymentbanner"
  | "contact details"
  | "invoice"
  | "schedule changes"
  | "thank you"
  | "ancillary"
  | "sports equipments"
  | "pet passengers"
  | "musical instruments"
  | "itinerary change"
  | "check-in"
  | "boarding passes"
  | "faq" // default owner: @vlado
  | "booking" // default owner: @dino.trojak
  | "general" // deprecated
  | "watchdog" // default owner: @andrej.skok
  | "search" // default owner: @vlado
  | "payment" // default owner: @dino.trojak
  | "content" // default owner: @dino.trojak
  | "cookies" // default owner: @toni.u
  | "account" // default owner: #login
  | "login" // default owner: @vbambuch
  | "exponea"
  | "itinerary alternatives"
  | "itinerary update"
  | "abandonment"
  | "journey change"
  | "baggage"
  | "priority boarding"
  | "price change"
  | "sold out"
  | "flights not checked"
  | "check flights"
  | "prebooking config"
  | "form"
  | "name corrector"
  | "passenger"
  | "insurance"
  | "billing details"
  | "promocode"
  | "deprecate"
  | "save booking"
  | "fare conditions"
  | "alternatives"
  | "travel tip"
  | "health declaration"
  | "ancillaries 3rd"
  | "check ancillaries"
  | "pre-booking"
  | "pre-pay-pnr"
  | "exit banner"
  | "flight mapper"
  | "thank you page"
  | "passenger edit"
  | "sports equipment"
  | "special assistance"
  | "pets"
  | "musical equipment"
  | "mapper"
  | "flights api"
  | "feedback"
  | "privacy policy"
  | "checkin"
  | "air help"
  | "blue ribbon bags"
  | "fast track"
  | "smart pass"
  | "shot"
  | "additional service"
  | "confirmation email"
  | "tickets"
  | "cancellation"
  | "timeline"
  | "pax ata"
  | "fare types"
  | "ancillaries"
  | "seating"
  | "pixel"
  | "tat banner"
  | "confirm payment"
  | "nps feedback"
  | "loyalty"
  | "compass explore page"
  | "refunds and cancellations"
  | "paid guarantee"
  | "riskified"
  | "generic banner"
  | "hidden cities"
  | "covid warning"
  | "itinerary"

export type LogEvent = ClientEvent<EventCategory>

export type CuckooEvent = {
  // name: string,
  category: EventCategory | TrackingModule
  errorType?: string
  action: string
  detail?: string
  owner?: string // a person who added the event; refer to the default owner if the owner defined in the event is missing / unavailable,
  version?: number // an integer that should be incremented each time the event is changed,
  triggerCondition?: string // when exactly is the event triggered,
  description?: string // general description of the event,
  destinations?: CuckooEventDestinations | null | undefined
}

const cuckooEventsUnchecked = {
  AUTH_PASSWORD_VERIFY_REQUEST: {
    category: "login",
    action: "verify user request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_TOKEN: {
    category: "login",
    action: "login with booking id request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_TOKEN_SUCCESS: {
    category: "login",
    action: "login with booking id success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_IATA_ERROR: {
    category: "login",
    action: "error",
    errorType: "login with booking IATA places data loading error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  AUTH_LOGIN_WITH_AUTH_TOKEN: {
    category: "login",
    action: "url auth token",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ASSERT_ERROR: {
    category: "general",
    action: "error",
    errorType: "assert error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BAD_REQUEST: {
    category: "general",
    action: "error",
    errorType: "bad request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_LOADING: {
    category: "general",
    action: "loading",
    destinations: {
      logmole: true,
      exponea: false,
      ga: true,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "The request to load the booking page.",
  },
  BOOKING_LOADING_ERROR: {
    category: "general",
    action: "error",
    errorType: "loading error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_LOADING_SUCCESS: {
    category: "general",
    action: "loading success",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_LOADING_SUCCESS_HIDDEN_FLIGHT: {
    category: "general",
    action: "loading success with hidden flight",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_LOADING_WITHOUT_TOKEN_ERROR: {
    category: "general",
    action: "error",
    errorType: "loading without token",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "Booking loading without token error",
  },
  BOOKING_LOADING_TRACKING_ERROR: {
    category: "general",
    action: "error",
    errorType: "tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_LOADING_WITHOUT_LOCATIONS: {
    category: "general",
    action: "loading without locations api",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SCREEN_LOADED: {
    category: "general",
    action: "booking screen has been loaded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_NOHIDDENFLIGHT_FIND_MORE: {
    category: "itinerary alternatives",
    action: "find more",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_NOHIDDENFLIGHT_CLOSE_MODAL: {
    category: "itinerary alternatives",
    action: "close modal",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "itinerary alternatives no hidden flight",
  },
  BOOKING_ALTERNATIVES_NOHIDDENFLIGHT_PICK_ALTERNATIVE: {
    category: "itinerary alternatives",
    action: "pick alternative",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "itinerary alternatives no hidden flight",
  },
  BOOKING_STARTED_NEW: {
    category: "general",
    action: "started",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "after we process data from first check_flights API call response",
    // description: "booking loader finished loading",
  },
  BOOKING_SUBMIT_NEW: {
    category: "general",
    action: "submit",
    destinations: {
      logmole: true,
      exponea: false,
      ga: true,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "handleSubmit promise is resolved",
    description:
      "after user clicks on pay and payment fields values are validated (not the payment itself)",
  },
  BOOKING_UPDATE_ERROR: {
    category: "itinerary update",
    action: "error",
    errorType: "check flights failed during update",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_UPDATE_ITINERARY_CHANGE: {
    category: "itinerary update",
    action: "changed after update",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "the itinerary has changed after update",
  },
  BOOKING_REFUND_POLICY: {
    category: "refund",
    action: "refund policy displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  /* Are we still using the below abandonment modals? If not, please remove the code from the repo */
  BOOKING_ABANDONMENT_MODAL_SHOWN: {
    category: "abandonment",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "abandonment modal" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ABANDONMENT_MODAL_CLOSED: {
    category: "abandonment",
    action: "modal closed",
    /*
    add attribute to this event modalType: "abandonment modal" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_REFUND_MODAL_SHOWN: {
    category: "refund",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "refund rules" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "refund rules modal displayed",
  },
  SCHEDULE_CHANGE_MODAL_SHOWN: {
    category: "journey change",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "Journey change" and keep the remaining payload of the event intact
    */
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SCHEDULE_CHANGE_CONFIRM: {
    category: "journey change",
    action: "confirmed",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_NO_BAGS_SEARCH: {
    category: "baggage",
    action: "no bag redirect to search",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_HAND_BAGS_PRIO_BOARDING_NOT_ORDERED_BUT_PAID: {
    category: "baggage",
    action: "error",
    errorType: "cabin bags with priority boarding added paid but not ordered",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRIORITY_BOARDING_MODAL_OPEN: {
    category: "priority boarding",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "Priority boarding" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRIORITY_BOARDING_MODAL_DISMISS: {
    category: "priority boarding",
    action: "modal closed",
    /*
    add attribute to this event modalType: "Priority boarding" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRIORITY_BOARDING_MODAL_SAVE: {
    category: "priority boarding",
    action: "saved",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description:
    //   "user added priority boarding by clicking on the save button on the priority boarding modal",
  },
  BOOKING_PRIORITY_BOARDING_SOLDOUT_BOX_SHOWN: {
    category: "priority boarding",
    action: "soldout box shown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRIORITY_BOARDING_SOLDOUT_MODAL_OPENED: {
    category: "priority boarding",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "priority boarding soldout" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRIORITY_BOARDING_SOLDOUT_MODAL_RETURN_TO_BAGGAGE: {
    category: "priority boarding",
    action: "clicked return to baggage",
    /*
    add attribute to this event modalType: "priority boarding soldout" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRIORITY_BOARDING_SOLDOUT_MODAL_CONTINUE: {
    category: "priority boarding",
    action: "clicked continue",
    /*
    add attribute to this event modalType: "priority boarding soldout" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRICE_CHANGE_ACCEPTABLE_DEVIATION: {
    category: "price change",
    action: "acceptable deviation",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRICE_CHANGE_DECREASE: {
    category: "price change",
    action: "decrease",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRICE_CHANGE_INCREASE_INITIAL_TRIGGERED: {
    category: "price change",
    action: "increase",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRICE_CHANGE_INCREASE_UPDATE_TRIGGERED: {
    category: "price change",
    action: "increase",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PRICE_CHANGE_DURING_PAYMENT: {
    category: "price change",
    action: "during payment",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FLIGHT_INVALID_INITIAL_TRIGGERED: {
    category: "check flights",
    action: "error",
    errorType: "flight invalid initial triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FLIGHT_INVALID_UPDATE_TRIGGERED: {
    category: "check flights",
    action: "error",
    errorType: "flight invalid update triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_SOLDOUT_MODAL_TRIGGERED: {
    category: "sold out",
    action: "modal displayed",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_NOT_CHECKED_MODAL_TRIGGERED: {
    category: "flights not checked",
    action: "modal displayed",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_MODAL_TRIGGERED: {
    category: "price change",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "booking alternatives due to price change" and keep the remaining payload of the event intact
    */
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_ACCEPT_PRICE_CHANGE: {
    category: "price change",
    action: "accepted",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_SELECT_ALTERNATIVE: {
    category: "price change",
    action: "alternative selected",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_GO_TO_SEARCH: {
    category: "itinerary alternatives",
    action: "go to search",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_TOKEN_CHANGE: {
    category: "general",
    action: "token change",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FORM_FIRST_INTERACTION: {
    category: "form",
    action: "first interaction",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FORM_INPUT: {
    category: "form",
    action: "input",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "change of value of a checkbox or blur event in a text field",
    // description: "leaving the text field in textboxes or clicking on a checkbox",
  },
  BOOKING_FORM_INPUT_MARKED_AS_CORRECT: {
    category: "form",
    action: "input marked as correct",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_NAME_CORRECTOR_API_TRIGGERED: {
    category: "name corrector",
    action: "api triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_NAME_CORRECTOR_API_RETURNED_RESULT: {
    category: "name corrector",
    action: "api returned result",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_NAME_CORRECTOR_API_ERROR: {
    category: "name corrector",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PASSENGER_ADD: {
    category: "passenger",
    action: "added",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PASSENGER_REMOVE: {
    category: "passenger",
    action: "removed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PASSENGER_CHANGE_HAND_BAGS: {
    category: "baggage",
    action: "change cabin bags",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PASSENGER_CHANGE_HOLD_BAGS: {
    category: "baggage",
    action: "change hold bags",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PASSENGER_INSURANCE_CHANGE: {
    category: "insurance",
    action: "selected",
    /*
    add attribute to this event if not present insuranceType: "basic, plus, premium" and insuranceProvide: "axa, cover-more, axa-us", passengerIndex: "1" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 2,
    owner: "marta.juric@kiwi.com",
    triggerCondition: "at the moment the customer selects the type of the insurance or removes it",
    // description: "",
  },
  BOOKING_FORM_INPUT_FIRST_PASSENGER: {
    category: "form",
    action: "first input",
    /*
    add attribute to this event if not present inputFieldType: "firstName, lastName, email, card detail" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  /* Is this still viable with iFrame? */
  BOOKING_FORM_INPUT_FIRST_PAYMENT: {
    category: "form",
    action: "first input",
    /*
    add attribute to this event if not present inputFieldType: "firstName, lastName, email, card detail" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FORM_INPUT_FIRST_CONTACT: {
    category: "form",
    action: "first input",
    /*
    add attribute to this event if not present inputFieldType: "firstName, lastName, email, card detail" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FORM_AUTOFILL_PASSENGER: {
    category: "form",
    action: "autofilled from stored data",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FORM_AUTOFILL_RESTORE: {
    category: "form",
    action: "autofilled from last session",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FORM_AUTOFILL_USER_EMAIL: {
    category: "form",
    action: "autofilled with logged user email",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BILLING_DETAILS_EXPANDED: {
    category: "billing details",
    action: "expanded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BILLING_DETAILS_COLLAPSED: {
    category: "billing details",
    action: "collapsed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_EXPANDED: {
    category: "promocode",
    action: "expanded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_COLLAPSED: {
    category: "promocode",
    action: "collapsed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_API_RESPONSE: {
    category: "promocode",
    action: "api response",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_API_TRIGGERED: {
    category: "promocode",
    action: "api trigger",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_ACCEPTED: {
    category: "promocode",
    action: "accepted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_ERROR: {
    category: "promocode",
    action: "error",
    errorType: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_ERROR_EMAIL_REQUIRED: {
    category: "promocode",
    action: "error",
    errorType: "email required",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PROMOCODE_ERROR_UNKNOWN: {
    category: "promocode",
    action: "error",
    errorType: "unknown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_STEP_CHANGE: {
    category: "general",
    action: "step change",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "after user initiates step change and step change is validated on fe",
    // description:
    //   "event is triggered right after user successfully initiates a redirect to another step (Continue button, Back button or by clicking on already visited step in the breadcrumbs)",
  },
  BOOKING_RISKIFIED_ERROR_INITIALIZING: {
    category: "riskified",
    action: "beacon script",
    detail: "error initializing",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: ""
  },
  BOOKING_RISKIFIED_ERROR_TRACKING: {
    category: "riskified",
    action: "error tracking",
    detail: "RISKX script is not loaded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: ""
  },
  BOOKING_JOURNEY_ITINERARY_NEGATIVE_LAYOVER: {
    category: "itinerary update",
    action: "error",
    errorType: "negative layover",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  /* deprecate: this should be available in boolean attribute "shortLayover" of started, step change, submit, success events and based on the attribute we know whether it is no bag itinerary or not */
  BOOKING_JOURNEY_ITINERARY_SHORT_LAYOVER: {
    category: "deprecate",
    action: "deprecate",
    destinations: {
      logmole: false,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: false,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SUBMIT_NOT_CHECKED: {
    category: "general",
    action: "error",
    errorType: "booking submitted but flights are not checked yet",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SUBMIT_VALIDATION_ERROR: {
    category: "form",
    action: "error",
    errorType: "validation error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_BOOKING: {
    category: "save booking",
    action: "api triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_ERROR_CANT_BOOK_ANYMORE: {
    category: "save booking",
    action: "error",
    errorType: "can't book anymore",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_ERROR_PROMOCODE_ALREADY_USED: {
    category: "save booking",
    action: "error",
    detail: "Promocode already used",
    errorType: "unrecognized response",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_ERROR_UNKNOWN: {
    category: "save booking",
    action: "error",
    errorType: "unknown",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_ERROR_GDS_RESERVATION_FAILED: {
    category: "save booking",
    action: "error",
    errorType: "GDS Reservation failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_ERROR_PAYMENT_CANCELLED: {
    category: "save booking",
    action: "error",
    errorType: "payment cancelled",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_WARNING_DUPLICATE_SHOWN: {
    category: "save booking",
    action: "modal displayed",
    /*
    add attribute to this event modalType: "duplicate warning" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_WARNING_DUPLICATE_DECIDED_TO_BOOK: {
    category: "save booking",
    action: "decided to book after duplicate warning",
    /*
    add attribute to this event modalType: "duplicate warning" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_WARNING_DUPLICATE_DECIDED_TO_GO_BACK: {
    category: "save booking",
    action: "decided to go back after duplicate warning",
    /*
    add attribute to this event modalType: "duplicate warning" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_WARNING_DUPLICATE_PASSENGERS_DECIDED_TO_BOOK: {
    category: "save booking",
    action: "duplicate passenger warning",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_WARNING_DUPLICATE_PASSENGERS_DECIDED_TO_GO_BACK: {
    category: "save booking",
    action: "duplicate passenger warning",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_PRICES_DIFFER_CANCELLING: {
    category: "save booking",
    action: "error",
    errorType:
      "Accepted price differs from charged amount, cancelling showing error and reloading page",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_PRICES_DIFFER_CONTINUING: {
    category: "save booking",
    action: "warning",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "Accepted price differs from charged amount, difference lower than 1 eur continuing",
  },
  BOOKING_SUMMARY_OPEN_REQUEST: {
    category: "general",
    action: "request to render booking summary",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SUMMARY_INVALID_FORM: {
    category: "general",

    action: "invalid form",
    errorType: "Booking summary not opened because of invalid form",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SUMMARY_SUBMIT: {
    category: "general",
    action: "summary submitted from modal",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_COMPLETE: {
    category: "general",
    action: "completed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FARE_CONDITIONS_API_TRIGGERED: {
    category: "fare conditions",
    action: "api triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FARE_CONDITIONS_API_ERROR: {
    category: "fare conditions",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_API_TRIGGERED: {
    category: "alternatives",
    action: "api triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_API_ERROR: {
    category: "alternatives",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ALTERNATIVES_SAME: {
    category: "alternatives",
    action: "same booking offered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_BOOKING_API_TRIGGERED: {
    category: "save booking",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_BOOKING_API_ERROR: {
    category: "save booking",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_BOOKING_SUCCESS: {
    category: "save booking",
    action: "successful",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVE_UNFINISHED_BOOKING: {
    category: "save booking",
    action: "unfinished booking submitted",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CONFIRM_PAYMENT_API_TRIGGERED: {
    category: "confirm payment",
    action: "confirm payment api triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CONFIRM_PAYMENT_API_ERROR: {
    category: "confirm payment",
    action: "error",
    errorType: "confirm payment api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_FLIGHTS_API_TRIGGERED: {
    category: "check flights",
    action: "api triggered",
    destinations: {
      logmole: false,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_UPDATE_API_BACKGROUND_TAB: {
    category: "booking",
    action: "api in background tab",
    destinations: {
      logmole: false,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
  },
  BOOKING_UPDATE_API_CHECKING_BACKGROUND_FAILED: {
    category: "booking",
    action: "api checking background tab failed",
    destinations: {
      logmole: false,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
  },
  BOOKING_PREBOOKING_CONFIG_RESPONSE: {
    category: "prebooking config",
    action: "response",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PREBOOKING_CONFIG_ERROR: {
    category: "prebooking config",
    action: "error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PREBOOKING_CONFIG_API_ERROR: {
    category: "prebooking config",
    action: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_FLIGHTS_API_SUCCESS: {
    category: "check flights",
    action: "api success",
    destinations: {
      logmole: false,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_FLIGHTS_API_SHOULD_CANCEL_PREVIOUS: {
    category: "check flights",
    action: "should cancel previous fetch",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_FLIGHTS_API_ERROR: {
    category: "check flights",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_FLIGHTS_API_SUPPRESSED: {
    category: "check flights",
    action: "api suppressed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_FLIGHTS_API_INITIAL_RESPONSE: {
    category: "check flights",
    action: "initial response",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ITINERARY_SUMMARY_EXPANDED: {
    category: "booking",
    action: "booking summary expanded",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
  },
  BOOKING_HEALTH_DECLARATION_MODAL_OPENED: {
    category: "health declaration",
    action: "modal opened",
    /*
    add attribute to this event modalType: "Travel tip" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_HEALTH_DECLARATION_MODAL_CONFIRMED: {
    category: "health declaration",
    action: "modal confirmed",
    /*
    add attribute to this event modalType: "Travel tip" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_HEALTH_DECLARATION_MODAL_CLOSED: {
    category: "health declaration",
    action: "modal closed",
    /*
    add attribute to this event modalType: "Travel tip" and keep the remaining payload of the event intact
    */
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  RESERVATION_FORM_VARIANT: {
    category: "ancillaries 3rd",
    action: "rendered booking variant",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_OFFERS_API_TRIGGERED: {
    category: "ancillaries 3rd",
    action: "BBE check offers api call started",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_OFFERS_API_SUCCESS: {
    category: "ancillaries 3rd",
    action: "BBE check offers api success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_OFFERS_API_ERROR: {
    category: "ancillaries 3rd",
    action: "BBE check offers api error",
    errorType: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_CHECK_OFFERS_API_DURATION: {
    category: "ancillaries 3rd",
    action: "BBE check offers api duration",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ANCILLARIES_3RD_API_TRIGGERED: {
    category: "ancillaries 3rd",
    action: "api call started",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ANCILLARIES_3RD_API_ERROR: {
    category: "ancillaries 3rd",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ANCILLARIES_3RD_API_SUCCESS: {
    category: "ancillaries 3rd",
    action: "ancillaries 3rd api success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_ANCILLARIES_3RD_API_DURATION: {
    category: "ancillaries 3rd",
    action: "duration",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PRE_PAY_PNR_API_TRIGGERED: {
    category: "pre-pay-pnr",
    action: "api triggered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PRE_PAY_PNR_API_SUCCESS: {
    category: "pre-pay-pnr",
    action: "api success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PRE_PAY_PNR_API_ERROR: {
    category: "pre-pay-pnr",
    action: "error",
    errorType: "api error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: false,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  // GENERAL
  // ===
  BOOKING_EXIT_BANNER_TRIGGERED: {
    category: "exit banner",
    action: "triggered",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_TAT_BANNER_DISPLAYED: {
    category: "tat banner",

    action: "displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_EXIT_BANNER_ACCEPTED: {
    category: "exit banner",
    action: "accepted",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_EXIT_BANNER_CLOSED: {
    category: "exit banner",
    action: "closed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  /* What is the difference to other priority boarding soldout events? */
  BOOKING_PB_SOLD_OUT_MARKED_FLIGHTS: {
    category: "priority boarding",
    action: "sold-out",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_REACT_ERROR: {
    category: "booking",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
  },
  GENERAL_UNSUPPORTED_CURRENCY: {
    category: "general",
    action: "unsupported currency",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_PAGE_LOADED: {
    category: "general",
    action: "page loaded",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "this event is triggered after re-render of the entire webpage is finished",
    // description:
    //   "loading of the entire page is finished (e.g. after refreshing, moving from search to booking, or moving from booking to mmb)",
  },
  GENERAL_PAGE_LEFT: {
    category: "general",
    action: "user left the page",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_PAGE_INITIALIZED: {
    category: "general",
    action: "page initialized",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_OPEN_MODAL: {
    category: "general",
    action: "open info modal",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_CORONA_BANNER_CLICK: {
    category: "general",
    action: "clicked corona alert banner",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_CORONA_BANNER_CLOSE: {
    category: "general",
    action: "CLOSE corona alert banner",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_PAGE_PARSING: {
    category: "general",
    action: "page parsing",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_PERFORMANCE: {
    category: "general",
    action: "performance client",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_PERFORMANCE_UNAVAILABLE: {
    category: "general",
    action: "performance unavailable",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_RENDER_ERROR: {
    category: "general",
    action: "error",
    errorType: "render error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_ERROR_UTM_LOCAL_STORAGE_LOAD: {
    category: "general",
    action: "error",
    errorType: "utm local storage load failer",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_ERROR_UTM_LOCAL_STORAGE_SET: {
    category: "general",
    action: "error",
    errorType: "utm local storage set failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_CURRENCY_SWITCHER_OPEN: {
    category: "general",
    action: "opend currency switcher",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_CURRENCY_SWITCHER_CHANGE: {
    category: "general",
    action: "changed currency",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  GENERAL_TRACKING_FAILED: {
    category: "general",
    action: "error",
    errorType: "tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_BAGS_UNSUPPORTED_FORMAT: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format for bag params mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_DOCUMENT_NEED_UNSUPPORTED: {
    category: "flight mapper",
    action: "error",
    errorType: "unrecognized input for mapper document need",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_AIRLINE: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of airline flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_LOCATION: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of location flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_TIME: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of time for flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_JOURNEY_UNSUPPORTED_FORMAT: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of journey for flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_FLIGHTS_HISTORY_UNSUPPORTED_FORMAT: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of flights history for flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_PENDING_FLIGHT_CHANGE_OPTIONS_UNSUPPORTED_FORMAT: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of pending flight change options for flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  INTERNAL_MAPPER_PENDING_REQUESTS_UNSUPPORTED_FORMAT: {
    category: "flight mapper",
    action: "error",
    errorType: "unsupported format of pending request params for flight mapper",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_FORM_INPUT_CORRECTED: {
    category: "form",
    action: "input corrected from suggestion",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAX_CHANGE_FORM_OPENED: {
    category: "passenger edit",
    action: "form open",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAX_CHANGE_FORM_SUBMIT: {
    category: "passenger edit",
    action: "form submit",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAX_CHANGE_OFFER: {
    category: "passenger edit",
    action: "offer",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_PENDING_REQUESTS_SUBMIT_REQUEST: {
    category: "general",
    action: "pending request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_PENDING_REQUESTS_SUBMIT_SUCCESS: {
    category: "general",
    action: "pending request success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_PENDING_REQUESTS_SUBMIT_ERROR: {
    category: "general",
    action: "error",
    errorType: "pending request error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_SPORTS_EQUIPMENT_PREV_STEP: {
    category: "sports equipment",
    action: "previous step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_SPORTS_EQUIPMENT_NEXT_STEP: {
    category: "sports equipment",
    action: "next step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_SPORTS_EQUIPMENT_UNAVAILABLE: {
    category: "sports equipment",
    action: "sports equipment unavailable displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_SPECIAL_ASSISTANCE_PREV_STEP: {
    category: "special assistance",
    action: "previous step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_SPECIAL_ASSISTANCE_NEXT_STEP: {
    category: "special assistance",
    action: "next step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_SPECIAL_ASSISTANCE_UNAVAILABLE: {
    category: "special assistance",
    action: "special assistance unavailable displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_PETS_PREV_STEP: {
    category: "pets",
    action: "previous step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_PETS_NEXT_STEP: {
    category: "pets",
    action: "next step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_PETS_UNAVAILABLE: {
    category: "pets",
    action: "pets unavailable displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_MUSICAL_EQUIPMENT_PREV_STEP: {
    category: "musical equipment",
    action: "previous step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_MUSICAL_EQUIPMENT_NEXT_STEP: {
    category: "musical equipment",
    action: "next step clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_MUSICAL_EQUIPMENT_UNAVAILABLE: {
    category: "musical equipment",
    action: "musical equipment unavailable displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MANAGE_MAPPER_UNSUPPORTED_STATUS: {
    category: "mapper",
    action: "unsupported status",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  DEEPLINK_FAILED_TO_DISPLAY: {
    category: "general",
    action: "error",
    errorType: "deeplink failed to display",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SEARCH_FLIGHTS_API_ERROR: {
    category: "flights api",
    action: "error",
    errorType: "FlightsAPI Request error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SEARCH_FLIGHTS_API_SUCCESS: {
    category: "flights api",
    action: "ok",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SEARCH_FLIGHTS_RESULTS_TOGGLE: {
    category: "search",
    action: "flight toggle",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SEARCH_FLIGHTS_RESULTS_CLICK: {
    category: "search",
    action: "booking link clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SEARCH_GO_TO_PARTNER_BOOKING: {
    category: "search",
    action: "redirect to partners booking",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_FEEDBACK_LOADING: {
    category: "feedback",
    action: "loading",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_FEEDBACK_SUBMIT: {
    category: "feedback",
    action: "submit",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_FEEDBACK_PREFILL: {
    category: "feedback",
    action: "user data prefilled",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_FEEDBACK_TOPIC_SELECTED: {
    category: "feedback",
    action: "topic selected",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_PRIVACY_POLICY_OPEN: {
    category: "privacy policy",
    action: "open",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_FEEDBACK_API_ERROR: {
    category: "fare conditions",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_COOKIES_CHANGED: {
    category: "cookies",
    action: "policy changed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CONTENT_COMPASS_ACTIVATE_CLICK: {
    category: "compass explore page",
    action: "Activate button clicked",
    version: 1,
  },
  CONTENT_COMPASS_POINTS_CHANGE: {
    category: "compass explore page",
    action: "Points slider value changed",
    version: 1,
  },
  CONTENT_COMPASS_TERMS_CLICK: {
    category: "compass explore page",
    action: "Terms and Conditions link clicked",
    version: 1,
  },
  CONTENT_COMPASS_FAQ_READ_MORE_CLICK: {
    category: "compass explore page",
    action: "FAQ Read More clicked",
    version: 1,
  },
  ANCILLARY_AIR_HELP_ADDED: {
    category: "air help",
    action: "added",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  ANCILLARY_AIR_HELP_REMOVED: {
    category: "air help",
    action: "removed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  ANCILLARY_BLUE_RIBBON_BAGS_ADDED: {
    category: "blue ribbon bags",
    action: "added",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  ANCILLARY_BLUE_RIBBON_BAGS_REMOVED: {
    category: "blue ribbon bags",
    action: "removed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  SHARE_ACTION_CLICK: {
    category: "general",
    action: "clicked on share button",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SEARCH_BREADCRUMB_CLICKED: {
    category: "search",
    action: "breadcrumb clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_NPS_FEEDBACK_OPEN: {
    category: "nps feedback",

    action: "show",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "Mike Rucky",
    // triggerCondition: "Click on feedback button",
    // description: "",
  },
  BOOKING_NPS_FEEDBACK_CLOSE: {
    category: "nps feedback",

    action: "close",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "Mike Rucky",
    // triggerCondition: "Close a feedback modal",
    // description: "",
  },
  BOOKING_NPS_FEEDBACK_VALUE_SELECT: {
    category: "nps feedback",

    action: "rate saved",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "Mike Rucky",
    // triggerCondition: "Click on value from feedback modal",
    // description: "",
  },
  BOOKING_NPS_FEEDBACK_EMAIL_INPUT: {
    category: "nps feedback",

    action: "email saved",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "Mike Rucky",
    // triggerCondition: "Click on feedback's modal submit button",
    // description: "",
  },
  BOOKING_NPS_FEEDBACK_TEXT_INPUT: {
    category: "nps feedback",

    action: "comment saved",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "Mike Rucky",
    // triggerCondition: "Click on feedback's modal submit button",
    // description: "",
  },
  BOOKING_SOCIAL_AUTH_LOGIN_CLICKED: {
    category: "booking",
    action: "social login button clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
  },
  EXIT_BANNER_SUBSCRIBE: {
    category: "exit banner",
    action: "newsletter subscribed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
    version: 1,
  },
  EXIT_BANNER_NEVER_SHOW: {
    category: "exit banner",
    action: "never show again clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
  },
  EXIT_BANNER_CLOSED: {
    category: "exit banner",
    action: "closed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
  },
  MMB_SHOT_RECEIVED: {
    category: "shot",
    action: "response received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_LOAD_BOOKING_STATUS_NULL: {
    category: "general",
    action: "load booking retry api",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_UNKNOWN_PENDING_REFUND_REASON: {
    category: "refund",
    action: "error",
    errorType: "pending refund unknown reason received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_ADDITIONAL_SERVICE_MAPPER_ERROR: {
    category: "additional service",
    action: "error",
    errorType: "Additional service failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_ADDITIONAL_BAGS_ZERO_PRICE: {
    category: "baggage",
    action: "error",
    errorType: "additional bags wrong price",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_ADDITIONAL_BAGS_WRONG_ABID: {
    category: "baggage",
    action: "error",
    errorType: "wrong additional bags abid received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_CONFIRMATION_EMAIL_DOWNLOAD: {
    category: "confirmation email",
    action: "downloaded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  // MMB REFUNDS
  MMB_REFUNDS_OPEN: {
    category: "refund",
    action: "modal opened",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_REFUNDS_CHOOSE_REASON: {
    category: "refund",
    action: "reason chosen",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_REFUNDS_RAF_SUBMIT: {
    category: "refund",
    action: "raf submitted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_REFUNDS_GUIDE_SUBMIT: {
    category: "refund",
    action: "guide submitted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_REFUNDS_MEDICAL_SUBMIT: {
    category: "refund",
    action: "medical submitted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_REFUNDS_CONTINUE: {
    category: "refund",
    action: "button continue clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_REFUNDS_BACK: {
    category: "refund",
    action: "button back clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_SET_CALCULATOR_TYPE: {
    category: "refund",
    action: "calculator type selected",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },

  MMB_MAPPER_SERVICES_UNKNOWN_CATEGORY: {
    category: "general",
    action: "unknown service category received",
    errorType: "mapper unknown service category received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  // MMB BAGS
  MMB_BAGGAGE_OPEN: {
    category: "baggage",
    action: "modal opened",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_BAGGAGE_ORDERED: {
    category: "baggage",
    action: "ordered",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      bigQuery: true,
      datadog: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  // BAGGAGE
  MMB_ADD_BAGS_PASSENGER_CHANGE_HAND_BAGS: {
    category: "baggage",
    action: "cabin bags changed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_ADD_BAGS_PASSENGER_CHANGE_HOLD_BAGS: {
    category: "baggage",
    action: "hold bags changed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  // BOOKING FARE_TYPES
  BOOKING_CLICK_ON_FARE_TYPES: {
    category: "fare types",
    action: "selected",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      datadog: true,
      bigQuery: true,
    },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVER_MODAL_OPEN: {
    category: "fare types",
    action: "saver modal opened",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVER_MODAL_SELECTED_FARETYPE: {
    category: "fare types",
    action: "saver modal - selected fare type",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_SAVER_MODAL_CLOSE: {
    category: "fare types",
    action: "saver modal closed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    // owner: "",
    version: 1,
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_FARE_TYPE_FETCH_ERROR: {
    category: "fare types",
    action: "fetching error",
    destinations: {
      logmole: true,
      exponea: false,
      ga: false,
      datadog: true,
      bigQuery: true,
    },
    version: 1,
  },
  MMB_ANCILLARIES_ERROR_XHR: {
    category: "ancillaries",
    action: "error",
    errorType: "Fetch ancillaries XHR failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  MMB_SEATING_ANCILLARIES_DATA_INCONSISTENCY: {
    category: "seating",
    action: "error",
    errorType: "inconsistent seating ancillaries data received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAGE_NOT_FOUND: {
    category: "general",
    action: "error",
    errorType: "page not found",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_PIXEL: {
    category: "pixel",
    action: "initialized from search",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_CABIN_BAGS_DISPLAYED: {
    category: "baggage",
    action: "paid cabin bags displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  PAID_CABIN_BAGS_AND_PRIORITY_BOARDING_DISPLAYED: {
    category: "baggage",
    action: "paid cabin bags with priority boarding displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  PAID_HOLD_BAGS_DISPLAYED: {
    category: "baggage",
    action: "paid hold bags displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  INSURANCE_DISPLAYED: {
    category: "insurance",
    action: "displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 2,
    owner: "marta.juric@kiwi.com",
    triggerCondition: "once the insurance offer is displayed to the customer",
  },
  INSURANCE_MODAL_OPENED: {
    category: "insurance",
    action: "modal displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    version: 2,
    owner: "marta.juric@kiwi.com",
    triggerCondition: "once the customer opens insurance details modal",
  },
  AXA_COVID_COVERAGE_TOOLTIP_DISPLAYED: {
    category: "insurance",
    action: "covid coverage tooltip displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true, datadog: true },
    owner: "marta.juric@kiwi.com",
    triggerCondition:
      "once the customer hovers or clicks the tooltip in covid coverage on travel plus option",
  },
  ANCILLARIES_ERROR_DISPLAYED: {
    category: "ancillaries",
    action: "error message displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BLUE_RIBBON_BAGS_DISPLAYED: {
    category: "blue ribbon bags",
    action: "displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  FARE_TYPES_DISPLAYED: {
    category: "fare types",
    action: "displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  STANDALONE_PRIORITY_BOARDING_DISPLAYED: {
    category: "priority boarding",
    action: "displayed standalone",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  AIR_HELP_DISPLAYED: {
    category: "air help",
    action: "displayed",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  ANCILLARY_DISPLAY_FAILED: {
    category: "ancillaries",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
    action: "received unrecognised deeplink ancillary category",
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  CABIN_BAGS_DISPLAYED: {
    category: "baggage",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    action: "cabin bags displayed",
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  HOLD_BAGS_DISPLAYED: {
    category: "baggage",
    destinations: { logmole: true, exponea: false, ga: true, bigQuery: true },
    action: "hold bags displayed",
    version: 1,
    // owner: "",
    // triggerCondition: "component rendered",
    // description: "",
  },
  MODAL_CLOSE: {
    category: "general",
    action: "modal closed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: false },
  },
  BOOKING_BILLING_DETAILS_PREFILL_ERROR: {
    category: "billing details",
    action: "prefill error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BILLING_DETAILS_ACCOUNT_PREFILL: {
    category: "billing details",
    action: "account prefilled",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BILLING_DETAILS_TOGGLED: {
    category: "billing details",
    action: "toggled",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BILLING_DETAILS_ADD_NEW: {
    category: "billing details",
    action: "add new",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_BILLING_DETAILS_STORED_NEW: {
    category: "billing details",
    action: "stored new",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_GUARANTEE_STEP_DISPLAYED: {
    category: "paid guarantee",
    action: "step displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_GUARANTEE_OPTION_SELECTED: {
    category: "paid guarantee",
    action: "option selected",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_GUARANTEE_MODAL_DISPLAYED: {
    category: "paid guarantee",
    action: "modal displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_GUARANTEE_MODAL_CLOSED: {
    category: "paid guarantee",
    action: "modal dismissed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_GUARANTEE_SFAQ_OPENED: {
    category: "paid guarantee",
    action: "sfaq opened",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  PAID_GUARANTEE_REFUND_FEE_DISPLAYED: {
    category: "paid guarantee",
    action: "refund fee displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
  },
  PAID_GUARANTEE_REFUND_FEE_2_DISPLAYED: {
    category: "paid guarantee",
    action: "refund fee 2 displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
  },
  ACCOUNT_TRAVELER_UPDATE_ERROR: {
    category: "account",
    action: "traveler update error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_GENERIC_BANNER_SFAQ_CLICKED: {
    category: "generic banner",
    action: "sfaq clicked",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_GENERIC_BANNER_DISPLAYED: {
    category: "generic banner",
    action: "displayed",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_HIDDEN_CITIES_WARNING_DISPLAYED: {
    category: "hidden cities",
    action: "mandatory modal displayed",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_HIDDEN_CITIES_WARNING_SEARCH: {
    category: "hidden cities",
    action: "warning rejected",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_HIDDEN_CITIES_WARNING_ACCEPTED: {
    category: "hidden cities",
    action: "warning accepted",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
    // owner: "",
    // triggerCondition: "",
    // description: "",
  },
  BOOKING_COVID_WARNING_DISPLAYED: {
    category: "covid warning",
    action: "mandatory modal displayed",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_COVID_WARNING_ACCEPTED: {
    category: "covid warning",
    action: "warning accepted",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_ITINERARY_STEP_DISPLAYED: {
    category: "itinerary",
    action: "step displayed",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_ITINERARY_NEXT_STEP_CLICKED: {
    category: "itinerary",
    action: "next step clicked",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_HIDDEN_CITIES_GUIDE_OPEN: {
    category: "hidden cities",
    action: "guide open",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_HIDDEN_CITIES_GUIDE_ENABLED: {
    category: "hidden cities",
    action: "guided version loaded",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_HIDDEN_CITIES_GUIDE_ACCEPTED: {
    category: "hidden cities",
    action: "guide accepted",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_HIDDEN_CITIES_GUIDE_SUBMIT: {
    category: "hidden cities",
    action: "guide submit feedback",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  BOOKING_FORM_ERROR_SHOWN: {
    category: "booking",
    action: "error shown",
    destinations: { datadog: true, logmole: true, exponea: false, ga: false, bigQuery: true },
    version: 1,
  },
  ITINERARY_SHOW_PNR_DISPLAYED: {
    category: "itinerary",
    action: "show PNR displayed",
    destinations: { logmole: false, exponea: false, ga: false, bigQuery: true, datadog: false },
    version: 1,
  },
  ITINERARY_SHOW_PNR_CLICKED: {
    category: "itinerary",
    action: "show PNR clicked",
    destinations: { logmole: false, exponea: false, ga: false, bigQuery: true, datadog: false },
    version: 1,
  },
} as const

type CuckooEventKeys = keyof typeof cuckooEventsUnchecked
export const cuckooEvents: Record<CuckooEventKeys, CuckooEvent> = cuckooEventsUnchecked
